import type { HTMLAttributes } from 'react';
import React from 'react';
import type {
  AverageReportType,
  GroupType,
  ProgressConsistencyGroupType,
  StudentStatusGroupType,
} from '../../../pages/types';
import { progress_consistency_groups, student_status_groups } from '../../../pages/types';
import type { ChartIconType } from '../types';
import type { ProgressConsistencyProps } from './types';
import { Tooltip } from '../../index';
import ProgressConsistencyWrapper from './components/ProgressConsistencyWrapper';
import { ReactComponent as ClassIconSvg } from '../icons/Class.svg';
import { APP_THEME } from '../../../styles/theme/styled/theme';
import { PROGRESS_LABEL_MAX_WIDTH, PROGRESS_LABEL_OFFSET, SCATTER_CHART_STROKE_WIDTH } from '../helpers/vars';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
interface Props extends ProgressConsistencyProps, HTMLProps {
  showOthersData: boolean;
  /**
   * Students inside group.
   */
  groups: (GroupType & AverageReportType)[];
  /**
   * Highlighted element(s) id.
   */
  highlightedIds?: number[] | null
  /**
   * Icon to be used for plotting
   */
  iconToUse: ChartIconType;
}

const SchoolProgressConsistencyGroup = (
  {
    showOthersData,
    headline,
    background,
    groups,
    highlightedIds,
    iconToUse,
    ...other
  }: Props,
): JSX.Element => {

  const getColor = (id: number): string => {
    if (showOthersData && highlightedIds && highlightedIds?.length > 0 && highlightedIds.includes(id)) {
      return APP_THEME.colors.primary;
    } else if ((progress_consistency_groups.indexOf(headline.toLowerCase() as ProgressConsistencyGroupType)
        >= progress_consistency_groups.indexOf('reliable')) ||
        (student_status_groups.indexOf(headline.toLowerCase() as StudentStatusGroupType)
            >= student_status_groups.indexOf('pro'))
    ) {
      return APP_THEME.colors.positive;
    } else {
      return APP_THEME.colors.negative;
    }
  };

  return (
    <ProgressConsistencyWrapper
      background={background}
      headline={headline}
      length={groups.length}
      {...other as HTMLProps}
    >
      {groups.length === 0 ? null : (
        groups.map((group) => (
          <Tooltip
            key={`consistency_${headline}_${group.id}`}
            tooltipMessage={group.fullName}
          >
            <div className={'flex items-center justify-center m-0.5'}>
              {iconToUse === 'class' ? (
                <ClassIconSvg
                  fill={getColor(group.id)}
                  strokeWidth={SCATTER_CHART_STROKE_WIDTH}
                />
              ) : (
                <div
                  className={'bg-white border-3 rounded-default truncate p-sm'}
                  style={{
                    width: PROGRESS_LABEL_MAX_WIDTH + PROGRESS_LABEL_OFFSET,
                    borderColor: getColor(group.id),
                  }}
                >
                  {group.fullName}
                </div>
              )}
            </div>
          </Tooltip>
        ))
      )}
    </ProgressConsistencyWrapper>
  );
};

SchoolProgressConsistencyGroup.displayName = 'SchoolProgressConsistencyGroup';

export default SchoolProgressConsistencyGroup;
